/**
 * Free shipping progress bar — Global-e re-localization guard.
 *
 * No cart-update sync logic here: the theme's Section Rendering API
 * already keeps this module current on every cart change. On the cart
 * page, main-cart-footer's .js-contents (which this module renders
 * inside) is part of CartItems.getSectionsToRender() in cart.js. In the
 * drawer, CartDrawerItems.getSectionsToRender() replaces the entire
 * .drawer__inner, which this module is also inside. Both already
 * re-render server-side on quantity change with zero extra JS.
 *
 * The one gap Liquid's server-side country gate can't cover: Global-e
 * can swap prices and geo-redirect a US/CA visitor to a different market
 * after first paint, without a page load. If that happens, this element
 * removes itself rather than keep showing a $60 US/CA promise a Global-e
 * checkout won't honor.
 */
class FreeShippingProgress extends HTMLElement {
  // Confetti (feature 323): once per cart change that crosses the free shipping amount. The first
  // bar of a page load only records the state, so loading a cart that is already unlocked is quiet.
  celebrateIfUnlocked() {
    const shared = (window.NativeShops = window.NativeShops || {});
    const unlocked = this.dataset.unlocked === 'true';
    const was = shared.fspUnlocked;
    shared.fspUnlocked = unlocked;
    if (was === false && unlocked && this.hasAttribute('data-confetti') && shared.fspConfetti) {
      // Let the drawer finish sliding in so the burst starts from the bar on screen. If the gap
      // quick view is still open (the cart page updates behind it), wait until it has closed.
      const play = () => setTimeout(() => shared.fspConfetti(), 350);
      if (document.querySelector('#FspQuickAdd[open]')) document.body.addEventListener('modalClosed', play, { once: true });
      else play();
    }
  }

  connectedCallback() {
    const check = () => {
      const country =
        window.NativeShops &&
        window.NativeShops.config &&
        window.NativeShops.config.countryCode;
      if (country && country !== this.dataset.country) {
        this.remove();
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', check, { once: true });
    } else {
      check();
    }

    // Gap product: warm the first design's page while the browser is idle, so the quick view
    // opens from memory. The cache (below) makes this a single request per page load.
    this.celebrateIfUnlocked();

    const quickView = this.querySelector('[data-fsp-quickview]');
    if (quickView) {
      const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 500));
      idle(() => {
        const fetchPage = window.NativeShops && window.NativeShops.fetchPage;
        if (fetchPage) fetchPage(quickView.dataset.productUrl).catch(() => {});
      });
    }
  }
}

customElements.define('free-shipping-progress', FreeShippingProgress);

/**
 * Gap product buttons (feature 323). The card is rendered by
 * snippets/free-shipping-progress.liquid inside this element, in the cart
 * page footer and in the cart drawer, and is replaced whenever those
 * re-render, so the handlers are delegated from the document.
 *
 *  - [data-fsp-add]        one-tap add of the card's variant, then the drawer
 *                          re-renders and opens, as after any add in the theme.
 *  - [data-fsp-quickview]  opens the theme's quick add modal (assets/quick-add.js,
 *                          loaded on first use) for the card's product, with the
 *                          consolidated-style swatches kept (data-keep-cross-links).
 *
 * On the cart page the drawer re-renders itself, and cart-items re-renders the
 * line items on a cart update, but nothing re-renders the footer (the bar and
 * this card), so that is refreshed here.
 */
(function () {
  const MODAL_ID = 'FspQuickAdd';

  // A short canvas burst from the bar (or the top middle if no bar is on screen). No library; the
  // canvas lives for about 2.5 seconds. Skipped for shoppers who prefer reduced motion.
  const confetti = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const bars = Array.from(document.querySelectorAll('free-shipping-progress'));
    const onScreen = bars
      .map((bar) => bar.getBoundingClientRect())
      .find((r) => r.width > 0 && r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth);
    const x0 = onScreen ? onScreen.left + onScreen.width / 2 : window.innerWidth / 2;
    const y0 = onScreen ? onScreen.top + onScreen.height / 2 : window.innerHeight / 3;

    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2000;';
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = window.innerWidth * ratio;
    canvas.height = window.innerHeight * ratio;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);

    const colors = ['#00AEEF', '#7FD6F7', '#FFC857', '#FF6B8B', '#5CCB93', '#FFFFFF'];
    const pieces = Array.from({ length: 110 }, () => {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.1;
      const speed = 6 + Math.random() * 9;
      return {
        x: x0,
        y: y0,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        w: 6 + Math.random() * 6,
        h: 4 + Math.random() * 5,
        rotation: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.3,
        color: colors[Math.floor(Math.random() * colors.length)],
      };
    });

    const start = performance.now();
    const frame = (now) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      ctx.globalAlpha = Math.max(0, 1 - Math.max(0, elapsed - 1600) / 900);
      pieces.forEach((piece) => {
        piece.vy += 0.28;
        piece.vx *= 0.99;
        piece.x += piece.vx;
        piece.y += piece.vy;
        piece.rotation += piece.spin;
        ctx.save();
        ctx.translate(piece.x, piece.y);
        ctx.rotate(piece.rotation);
        ctx.fillStyle = piece.color;
        ctx.fillRect(-piece.w / 2, -piece.h / 2, piece.w, piece.h);
        ctx.restore();
      });
      if (elapsed < 2500) requestAnimationFrame(frame);
      else canvas.remove();
    };
    requestAnimationFrame(frame);
  };

  // Warm the main product image from a cached page, with the same sizes/srcset the modal will use,
  // so the browser fetches the candidate it will actually show. DOMParser pages are inert, so
  // nothing in them loads until this does.
  const warmedImages = new Set();
  const warmImage = (path, html) => {
    if (warmedImages.has(path)) return;
    warmedImages.add(path);
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.product__media img');
    if (!source) return;
    const image = new Image();
    image.decoding = 'async';
    if (source.getAttribute('sizes')) image.sizes = source.getAttribute('sizes');
    if (source.getAttribute('srcset')) image.srcset = source.getAttribute('srcset');
    image.src = source.getAttribute('src') || '';
  };

  // One request per product page for the life of the page. Holds the promise, so a page that
  // is still loading is shared, and a failed load is dropped so it can be tried again.
  // Used by the gap product's quick view (assets/quick-add.js) for opening and for switching designs.
  const pageCache = new Map();
  const namespace = (window.NativeShops = window.NativeShops || {});
  namespace.fspConfetti = confetti;

  // On the cart page an upsell add updates the page in place (cart-items re-renders the lines on
  // the cartUpdate event, the footer is refreshed below) and must not open the drawer. The drawer's
  // own sections are still brought up to date, exactly as renderContents does, just without open().
  namespace.fspOnCartPage = () => !!document.getElementById('main-cart-items');
  namespace.fspRenderQuietly = (response) => {
    const cart = document.querySelector('cart-drawer');
    if (!cart || !response || !response.sections) return;
    cart.classList.remove('is-empty');
    const inner = cart.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
    cart.getSectionsToRender().forEach((section) => {
      const element = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (element && response.sections[section.id]) {
        element.innerHTML = cart.getSectionInnerHTML(response.sections[section.id], section.selector);
      }
    });
  };
  namespace.fetchPage = (url) => {
    const path = new URL(url, window.location.origin).pathname;
    if (!pageCache.has(path)) {
      pageCache.set(
        path,
        fetch(path)
          .then((response) => {
            if (!response.ok) throw new Error(`${response.status} ${path}`);
            return response.text();
          })
          .then((text) => {
            const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 200));
            idle(() => warmImage(path, text));
            return text;
          })
          .catch((error) => {
            pageCache.delete(path);
            throw error;
          })
      );
    }
    return pageCache.get(path);
  };

  const ensureStylesheet = (href) => {
    if (!href || document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  };

  const ensureScript = (src) =>
    new Promise((resolve, reject) => {
      if (customElements.get('quick-add-modal')) return resolve();
      if (!document.querySelector(`script[src="${src}"]`)) {
        const script = document.createElement('script');
        script.src = src;
        script.onerror = reject;
        document.head.appendChild(script);
      }
      customElements.whenDefined('quick-add-modal').then(resolve);
    });

  // Built from an HTML string, not createElement: ModalDialog's constructor looks for the
  // close button, so the children must exist when the element is upgraded.
  const ensureModal = (card) => {
    let modal = document.getElementById(MODAL_ID);
    if (modal) return modal;
    const holder = document.createElement('div');
    holder.innerHTML =
      `<quick-add-modal id="${MODAL_ID}" class="quick-add-modal" data-keep-cross-links>` +
      '<div role="dialog" aria-modal="true" class="quick-add-modal__content global-settings-popup" tabindex="-1">' +
      '<div class="quick-add-modal-header">' +
      '<button id="ModalClose-fsp" type="button" class="quick-add-modal__toggle">' +
      '<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
      '</button></div>' +
      '<div id="QuickAddInfo-fsp" class="quick-add-modal__content-info"></div></div></quick-add-modal>';
    modal = holder.firstElementChild;
    modal.querySelector('[role="dialog"]').setAttribute('aria-label', card.dataset.dialogLabel || '');
    modal.querySelector('#ModalClose-fsp').setAttribute('aria-label', card.dataset.closeLabel || 'Close');
    document.body.appendChild(modal);
    return modal;
  };

  const openQuickView = (button) => {
    if (button.getAttribute('aria-disabled') === 'true') return;
    const card = button.closest('[data-fsp-gap]');
    if (!card) return;
    ensureStylesheet(card.dataset.quickAddCss);
    ensureScript(card.dataset.quickAddSrc)
      .then(() => {
        const modal = ensureModal(card);
        return customElements.whenDefined('quick-add-modal').then(() => modal.show(button));
      })
      .catch((error) => console.error(error));
  };

  const addToBag = (button) => {
    if (button.getAttribute('aria-disabled') === 'true') return;
    const cart = document.querySelector('cart-drawer');
    if (!cart || typeof fetchConfig !== 'function') {
      window.location = window.routes.cart_url;
      return;
    }
    button.setAttribute('aria-disabled', 'true');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    const formData = new FormData();
    formData.append('id', button.dataset.variantId);
    formData.append('quantity', 1);
    formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;
    cart.setActiveElement(button);

    fetch(window.routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error(response.description || response.message);
          return;
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fsp-gap', productVariantId: button.dataset.variantId, cartData: response });
        if (namespace.fspOnCartPage()) namespace.fspRenderQuietly(response);
        else cart.renderContents(response);
      })
      .catch((error) => console.error(error))
      .finally(() => button.removeAttribute('aria-disabled'));
  };

  // Warm the page as soon as the button is approached, ahead of the click.
  ['pointerover', 'focusin', 'touchstart'].forEach((type) =>
    document.addEventListener(
      type,
      (event) => {
        const quick = event.target.closest && event.target.closest('[data-fsp-quickview]');
        if (quick) namespace.fetchPage(quick.dataset.productUrl).catch(() => {});
      },
      { passive: true }
    )
  );

  document.addEventListener('click', (event) => {
    const add = event.target.closest('[data-fsp-add]');
    if (add) {
      event.preventDefault();
      addToBag(add);
      return;
    }
    const quick = event.target.closest('[data-fsp-quickview]');
    if (quick) {
      event.preventDefault();
      openQuickView(quick);
    }
  });

  const refreshCartFooter = () => {
    const footer = document.getElementById('main-cart-footer');
    if (!footer || !footer.dataset.id) return;
    fetch(`${window.location.pathname}?section_id=${footer.dataset.id}`)
      .then((response) => response.text())
      .then((text) => {
        const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('.js-contents');
        const current = footer.querySelector('.js-contents');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      })
      .catch((error) => console.error(error));
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'cart-items') return;
      refreshCartFooter();
    });
  }
})();
